import { useQuery } from "@tanstack/react-query";
import { createContext, use, useEffect, useState } from "react";
import type { JSX, ReactNode } from "react";
import { useApiClient } from "../../api/ApiContext";
import { platformAccountSchema } from "../../api/platformSchemas";
import { useOffline } from "../../offline/OfflineContext";
import { hasTenantDataOnDevice } from "../../offline/unsentEntries";
import { useAuth } from "../../shared/auth/AuthContext";
import { decidePlatformAccess, platformAccessOf } from "./platformAccess";
import type {
  PlatformAccess,
  PlatformAccessDecision,
  PlatformAccessMarker,
} from "./platformAccess";
import { platformQueryKeys } from "./platformQueryKeys";

const PlatformAccessMarkerContext = createContext<PlatformAccessMarker | null>(null);

/** Properties of {@link PlatformAccessProvider}. */
export interface PlatformAccessProviderProps {
  /** The marker of this device, composed in the composition root. */
  readonly marker: PlatformAccessMarker;
  /** Child elements. */
  readonly children: ReactNode;
}

/**
 * Provides the marker that remembers whether the signed-in person is operator staff (F-69).
 *
 * @param props - Component properties.
 * @returns The provider element.
 */
export function PlatformAccessProvider(props: PlatformAccessProviderProps): JSX.Element {
  const provider = (
    <PlatformAccessMarkerContext value={props.marker}>{props.children}</PlatformAccessMarkerContext>
  );

  return provider;
}

/**
 * Returns the marker of this device.
 *
 * @returns The marker.
 * @throws Error if no {@link PlatformAccessProvider} is present.
 */
function usePlatformAccessMarker(): PlatformAccessMarker {
  const marker = use(PlatformAccessMarkerContext);

  if (marker === null) {
    throw new Error("usePlatformAccessMarker must be used inside a PlatformAccessProvider.");
  }

  return marker;
}

/** The access of the signed-in person as the gate needs it (F-84). */
export interface PlatformAccessState {
  /** The decision. */
  readonly access: PlatformAccessDecision;
  /** The error of the last failed request, or `null` (no failure, or offline without a request). */
  readonly error: Error | null;
  /** Whether a request is running. */
  readonly retrying: boolean;
  /** Asks the server again. */
  readonly retry: () => void;
}

/** The last settled failure of the query: its error, or `null` while waiting offline. */
interface SettledFailure {
  /** The error of the request, or `null`. */
  readonly error: Error | null;
}

/**
 * Returns the access of the signed-in person with the state of `GET /platform/v1/me` (F-69, F-84):
 * the platform roles of the answer, remembered on this device for the next start, or the remembered
 * access while the server has not answered. Every answer replaces the remembered access, so a granted
 * or removed role applies on the next answer. Without an answer and without a remembered access a
 * failed request or a missing connection leads to the application of the tenants if the device holds
 * tenant data of the person, else to `unavailable`. Only settled outcomes change it: a
 * request that runs after a failure keeps `unavailable` (as `useRegistration` does, ADR-0206), so the
 * screen does not change while the server is asked again and no observer is mounted anew per answer.
 *
 * @returns The state.
 */
export function usePlatformAccessState(): PlatformAccessState {
  const client = useApiClient();
  const marker = usePlatformAccessMarker();
  const { user } = useAuth();
  const subject = user?.subject ?? null;
  const query = useQuery({
    queryKey: platformQueryKeys.account,
    queryFn: ({ signal }) => client.get("/platform/v1/me", platformAccountSchema, { signal }),
    enabled: subject !== null,
    staleTime: 5 * 60_000,
    retry: 1,
  });
  const [failure, setFailure] = useState<SettledFailure | null>(null);
  // `undefined`: nothing settled, the last outcome stays. A paused request waits for a connection.
  const settled: SettledFailure | null | undefined = query.isSuccess
    ? null
    : query.isError && !query.isFetching
      ? { error: query.error }
      : query.isPaused
        ? { error: failure?.error ?? null }
        : undefined;

  // Adjusting state to a new outcome during rendering, as React recommends instead of an effect.
  if (settled !== undefined && !sameFailure(settled, failure)) {
    setFailure(settled);
  }

  const roles = query.data?.roles;
  const remembered = subject === null ? null : marker.read(subject);
  // Asked only when neither an answer nor a remembered access decides, so pages inside the area
  // never read the store.
  const needsTenantData =
    subject !== null && failure !== null && roles === undefined && remembered === null;
  const tenantDataOnDevice = useTenantDataOnDevice(needsTenantData ? subject : null);
  const decided = decidePlatformAccess(
    { roles, failed: failure !== null, tenantDataOnDevice },
    remembered,
  );

  useEffect(() => {
    if (subject !== null && roles !== undefined) {
      marker.remember(subject, platformAccessOf(roles));
    }
  }, [marker, subject, roles]);

  const state: PlatformAccessState = {
    // Without a signed-in person there is nothing to decide (the routes require a sign-in).
    access: subject === null ? "none" : decided,
    error: failure?.error ?? null,
    retrying: query.isFetching,
    retry: () => {
      void query.refetch();
    },
  };

  return state;
}

/** Whether the device holds tenant data of one person. */
interface TenantDataOnDevice {
  /** The person. */
  readonly subject: string;
  /** Whether such data exists. */
  readonly found: boolean;
}

/**
 * Reads whether the device holds data of a person from the application of the tenants (F-84).
 *
 * @param subject - The person, or `null` when nothing needs to be read.
 * @returns The result for this person, or `null` while it is read or not needed. A store that cannot
 * be read counts as no data.
 */
function useTenantDataOnDevice(subject: string | null): boolean | null {
  const { store } = useOffline();
  const [read, setRead] = useState<TenantDataOnDevice | null>(null);

  useEffect(() => {
    if (subject === null) {
      return undefined;
    }

    let active = true;
    const settle = (found: boolean): void => {
      if (active) {
        setRead({ subject, found });
      }
    };

    hasTenantDataOnDevice(store, subject).then(settle, () => {
      settle(false);
    });

    const stop = (): void => {
      active = false;
    };

    return stop;
  }, [store, subject]);

  const found = subject !== null && read?.subject === subject ? read.found : null;

  return found;
}

/**
 * Compares two settled outcomes of the query.
 *
 * @param first - The first outcome.
 * @param second - The second outcome.
 * @returns Whether both are no failure or failures with the same error.
 */
function sameFailure(first: SettledFailure | null, second: SettledFailure | null): boolean {
  const same = first === null || second === null ? first === second : first.error === second.error;

  return same;
}

/**
 * Returns what the signed-in person may do in the operator area (F-69), for the pages inside the
 * area: the gate has decided before they show, so `unavailable` counts as no access there (nothing to
 * change is offered). The gate itself uses {@link usePlatformAccessState}.
 *
 * @returns The access, or `loading` while the first answer is awaited without a remembered access.
 */
export function usePlatformAccess(): PlatformAccess | "loading" {
  const { access } = usePlatformAccessState();
  const forPages: PlatformAccess | "loading" = access === "unavailable" ? "none" : access;

  return forPages;
}
